import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Flag, Target, Share2, Sparkles, X } from "lucide-react";
import {
  getActivationStep,
  markCreatorEntered,
  subscribeActivation,
  type ActivationStep,
} from "@/lib/activation";
import { trackEvent } from "@/lib/audit";
import { useAuth } from "@/hooks/useAuth";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";
import { WOW_FOCUS_EVENT } from "@/lib/wow/predictionWow";

const DISMISS_KEY = "vs:activation:dismissed";

type Surface = "home" | "nation_wars" | "quinielas" | "creator_studio";

/**
 * POST-NATIONGATE ACTIVATION™ banner.
 *
 * Reads the current activation step and renders the next CTA in the
 * sequence:
 *   nation → prediction → share → creator → done
 *
 * The "nation" step is handled by NationGate itself; this banner only
 * surfaces from step "prediction" onward, so it complements (does not
 * duplicate) NationGate. Auto-marks "creator" when mounted on Creator
 * Studio.
 */
export function ActivationBanner({ surface }: { surface: Surface }) {
  const { isAuthenticated } = useAuth();
  const { nation } = useSelectedNation();
  const [step, setStep] = useState<ActivationStep>(() => getActivationStep());
  // QA-FINAL-02 — read localStorage in an effect to keep SSR and the
  // first client render byte-identical (no hydration mismatch).
  const [dismissed, setDismissed] = useState<boolean>(false);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* noop */ }
  }, []);
  const [pulse, setPulse] = useState(false);

  useEffect(() => subscribeActivation(() => setStep(getActivationStep())), []);

  // P0-WOW-02: focus-pulse when the WOW moment fires.
  useEffect(() => {
    const onFocus = () => {
      setDismissed(false);
      try { window.localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
      setPulse(true);
      window.setTimeout(() => setPulse(false), 2400);
    };
    window.addEventListener(WOW_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(WOW_FOCUS_EVENT, onFocus);
  }, []);

  // Auto-mark creator step once the user reaches Creator Studio.
  useEffect(() => {
    if (surface === "creator_studio" && isAuthenticated) markCreatorEntered();
  }, [surface, isAuthenticated]);

  if (!isAuthenticated) return null;
  if (step === "done" || step === "nation") return null;
  if (dismissed) return null;

  const baseConfig = STEP_CONFIG[step];
  // P0-WOW-02: share step emphasizes nation recruitment.
  const config = step === "share"
    ? {
        ...baseConfig,
        title: nation
          ? `${getNationFlag(nation)} Ayuda a ${getNationName(nation)} a crecer.`
          : baseConfig.title,
        subtitle: "Cada recluta suma puntos por tu bandera.",
        cta: "Recluta otro fan",
      }
    : baseConfig;
  const onClick = () => {
    void trackEvent("activation_cta_clicked", { step, surface });
  };
  const onDismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
    void trackEvent("activation_step_advanced", { step, surface, action: "dismissed" });
  };

  const Icon = config.icon;

  return (
    <div
      data-activation-banner
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-r from-primary/15 via-primary/5 to-accent/15 p-4 shadow-lg transition ${pulse ? "border-primary ring-2 ring-primary/60 animate-pulse" : "border-primary/30"}`}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar"
        className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground hover:bg-background/40 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-center gap-3 pr-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" />
            Activación · paso {config.index} de 4
          </p>
          <p className="truncate text-sm font-bold text-foreground">{config.title}</p>
          <p className="truncate text-xs text-muted-foreground">{config.subtitle}</p>
        </div>
        <Link
          to={config.to}
          onClick={onClick}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-xs font-black text-primary-foreground shadow"
        >
          {config.cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

const STEP_CONFIG: Record<Exclude<ActivationStep, "nation" | "done">, {
  index: number;
  title: string;
  subtitle: string;
  cta: string;
  to: "/quinielas" | "/nation-wars" | "/creator-studio";
  icon: typeof Flag;
}> = {
  prediction: {
    index: 2,
    title: "Tu nación ya está lista para competir.",
    subtitle: "Haz tu primera predicción y empieza a sumar puntos.",
    cta: "Haz tu primera predicción",
    to: "/quinielas",
    icon: Target,
  },
  share: {
    index: 3,
    title: "Ya predijiste. Ahora multiplica tu nación.",
    subtitle: "Cada share suma reclutas para tu bandera.",
    cta: "Comparte tu nación",
    to: "/nation-wars",
    icon: Share2,
  },
  creator: {
    index: 4,
    title: "Eres oficialmente parte del Mundial.",
    subtitle: "Conviértete en Creator y desbloquea recompensas.",
    cta: "Aplica como Creator",
    to: "/creator-studio",
    icon: Flag,
  },
};